import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Copy, Calendar, Clock, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const timezones = [
  "UTC", "America/New_York", "America/Los_Angeles", "America/Chicago",
  "Europe/London", "Europe/Paris", "Asia/Tokyo", "Asia/Shanghai",
  "Asia/Kolkata", "Australia/Sydney"
];

export default function CronEditor() {
  const navigate = useNavigate();
  const [expression, setExpression] = useState("* * * * *");
  const [timezone, setTimezone] = useState("UTC");
  const [result, setResult] = useState(null);
  const [examples, setExamples] = useState([]);
  const [loading, setLoading] = useState(false);

  // Visual builder state
  const [minute, setMinute] = useState("*");
  const [hour, setHour] = useState("*");
  const [day, setDay] = useState("*");
  const [month, setMonth] = useState("*");
  const [weekday, setWeekday] = useState("*");

  useEffect(() => {
    fetchExamples();
  }, []);

  useEffect(() => {
    // Update expression from visual builder
    const expr = `${minute} ${hour} ${day} ${month} ${weekday}`;
    setExpression(expr);
  }, [minute, hour, day, month, weekday]);

  const fetchExamples = async () => {
    try {
      const response = await axios.get(`${API}/cron/examples`);
      setExamples(response.data.examples);
    } catch (error) {
      console.error("Failed to fetch examples");
    }
  };

  const handleValidate = async () => {
    if (!expression.trim()) {
      toast.error("Please enter a cron expression");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/cron/validate`, {
        expression,
        timezone
      });
      setResult(response.data);
      toast.success("Valid cron expression!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid cron expression");
      setResult(null);
    }
    setLoading(false);
  };

  const loadExample = (expr) => {
    setExpression(expr);
    const parts = expr.split(" ");
    if (parts.length === 5) {
      setMinute(parts[0]);
      setHour(parts[1]);
      setDay(parts[2]);
      setMonth(parts[3]);
      setWeekday(parts[4]);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0b] via-[#0f0f12] to-[#0a0a0b]">
      <div className="border-b border-zinc-800/50 backdrop-blur-xl bg-zinc-900/30">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center space-x-4">
            <Button
              data-testid="back-home-btn"
              variant="ghost"
              size="sm"
              onClick={() => navigate("/")}
              className="text-zinc-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className="text-2xl font-bold text-white" style={{fontFamily: 'Space Grotesk'}}>Cron Expression Editor</h1>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Main Editor */}
        <Card className="bg-zinc-900/50 border-zinc-800 p-6 mb-6">
          <div className="space-y-6">
            <div>
              <Label htmlFor="expression" className="text-zinc-400 mb-2 block">Cron Expression</Label>
              <div className="flex items-center space-x-2">
                <Input
                  data-testid="cron-input"
                  id="expression"
                  value={expression}
                  onChange={(e) => setExpression(e.target.value)}
                  placeholder="* * * * *"
                  className="flex-1 bg-zinc-800 border-zinc-700 text-white font-mono text-lg"
                />
                <Button
                  onClick={() => copyToClipboard(expression)}
                  variant="outline"
                  size="sm"
                  className="border-zinc-700 hover:bg-zinc-800"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-zinc-500 mt-2">Format: minute hour day month weekday</p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="timezone" className="text-zinc-400 mb-2 block">Timezone</Label>
                <select
                  data-testid="timezone-select"
                  id="timezone"
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-md px-3 py-2"
                >
                  {timezones.map(tz => (
                    <option key={tz} value={tz}>{tz}</option>
                  ))}
                </select>
              </div>
            </div>

            <Button
              data-testid="validate-btn"
              onClick={handleValidate}
              disabled={loading}
              className="w-full bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white"
            >
              {loading ? "Validating..." : "Validate & Calculate Schedule"}
            </Button>
          </div>
        </Card>

        {/* Visual Builder */}
        <Card className="bg-zinc-900/50 border-zinc-800 p-6 mb-6">
          <h2 className="text-xl font-bold text-white mb-4" style={{fontFamily: 'Space Grotesk'}}>Visual Builder</h2>
          <div className="grid md:grid-cols-5 gap-4">
            <div>
              <Label htmlFor="minute" className="text-zinc-400 mb-2 block text-sm">Minute</Label>
              <Input
                data-testid="minute-input"
                id="minute"
                value={minute}
                onChange={(e) => setMinute(e.target.value)}
                placeholder="*"
                className="bg-zinc-800 border-zinc-700 text-white font-mono"
              />
              <p className="text-xs text-zinc-600 mt-1">0-59</p>
            </div>
            <div>
              <Label htmlFor="hour" className="text-zinc-400 mb-2 block text-sm">Hour</Label>
              <Input
                data-testid="hour-input"
                id="hour"
                value={hour}
                onChange={(e) => setHour(e.target.value)}
                placeholder="*"
                className="bg-zinc-800 border-zinc-700 text-white font-mono"
              />
              <p className="text-xs text-zinc-600 mt-1">0-23</p>
            </div>
            <div>
              <Label htmlFor="day" className="text-zinc-400 mb-2 block text-sm">Day</Label>
              <Input
                data-testid="day-input"
                id="day"
                value={day}
                onChange={(e) => setDay(e.target.value)}
                placeholder="*"
                className="bg-zinc-800 border-zinc-700 text-white font-mono"
              />
              <p className="text-xs text-zinc-600 mt-1">1-31</p>
            </div>
            <div>
              <Label htmlFor="month" className="text-zinc-400 mb-2 block text-sm">Month</Label>
              <Input
                data-testid="month-input"
                id="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                placeholder="*"
                className="bg-zinc-800 border-zinc-700 text-white font-mono"
              />
              <p className="text-xs text-zinc-600 mt-1">1-12</p>
            </div>
            <div>
              <Label htmlFor="weekday" className="text-zinc-400 mb-2 block text-sm">Weekday</Label>
              <Input
                data-testid="weekday-input"
                id="weekday"
                value={weekday}
                onChange={(e) => setWeekday(e.target.value)}
                placeholder="*"
                className="bg-zinc-800 border-zinc-700 text-white font-mono"
              />
              <p className="text-xs text-zinc-600 mt-1">0-6 (Sun-Sat)</p>
            </div>
          </div>
          <div className="mt-4 p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
            <div className="flex items-start space-x-2">
              <Info className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-zinc-400">
                <p className="font-semibold text-white mb-1">Special Characters:</p>
                <ul className="space-y-1">
                  <li><code className="text-cyan-400">*</code> - any value</li>
                  <li><code className="text-cyan-400">,</code> - value list separator (e.g., 1,3,5)</li>
                  <li><code className="text-cyan-400">-</code> - range of values (e.g., 1-5)</li>
                  <li><code className="text-cyan-400">/</code> - step values (e.g., */5 = every 5)</li>
                </ul>
              </div>
            </div>
          </div>
        </Card>

        {/* Result */}
        {result && (
          <div className="space-y-6">
            <Card className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/30 p-6">
              <div className="flex items-center space-x-3 mb-4">
                <Calendar className="w-6 h-6 text-purple-400" />
                <h3 className="text-xl font-bold text-white" style={{fontFamily: 'Space Grotesk'}}>Schedule Description</h3>
              </div>
              <p className="text-2xl text-white font-semibold">{result.description}</p>
              <p className="text-sm text-zinc-400 mt-2">Expression: <code className="text-purple-400">{result.expression}</code></p>
            </Card>

            <Card className="bg-zinc-900/50 border-zinc-800 p-6">
              <div className="flex items-center space-x-3 mb-4">
                <Clock className="w-6 h-6 text-cyan-400" />
                <h3 className="text-lg font-bold text-white" style={{fontFamily: 'Space Grotesk'}}>Next 10 Executions</h3>
              </div>
              <div className="space-y-2">
                {result.next_runs.map((run, index) => (
                  <div
                    key={index}
                    data-testid={`next-run-${index}`}
                    className="flex items-center justify-between p-3 bg-zinc-800/50 rounded border border-zinc-700 hover:bg-zinc-800 transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="text-sm text-zinc-500 w-6">{index + 1}.</div>
                      <div>
                        <div className="text-white font-mono text-sm">{run.datetime}</div>
                        <div className="text-xs text-zinc-500">{result.timezone}</div>
                      </div>
                    </div>
                    <div className="text-sm text-cyan-400">{run.relative}</div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* Examples */}
        <Card className="bg-zinc-900/50 border-zinc-800 p-6 mt-6">
          <h3 className="text-lg font-bold text-white mb-4" style={{fontFamily: 'Space Grotesk'}}>Common Examples</h3>
          <div className="grid md:grid-cols-2 gap-3">
            {examples.map((example, index) => (
              <div
                key={index}
                data-testid={`example-${index}`}
                onClick={() => loadExample(example.expression)}
                className="p-4 bg-zinc-800/50 rounded border border-zinc-700 hover:bg-zinc-800 cursor-pointer transition-colors group"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <code className="text-purple-400 font-mono text-sm">{example.expression}</code>
                    <p className="text-sm text-zinc-400 mt-1">{example.description}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => {
                      e.stopPropagation();
                      copyToClipboard(example.expression);
                    }}
                  >
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
